import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { isTickableAddon, listPriceComponents, listPriceRules, setComponentInternet, setComponentUpstreamItem, type PriceComponent } from "../../api/priceList";
import { getWallet, matchUpstreamItem, upstreamItemsOf, type WalletItem } from "../../api/wallet";
import { gstPriceSuffix } from "../../domain/gstChoice";
import { componentsView, type ComponentEntry, type Gate } from "../../api/components";

function errorTitle(e: Error): string {
  if (e instanceof ApiError) {
    switch (e.kind) {
      case "unauthenticated":
        return "Your sign-in has expired -- sign in again";
      case "forbidden":
        return "Refused -- this namespace is outside yours, or your role may not read price lists";
      case "not-found":
        return "The gateway does not publish the price list yet";
      default:
        break;
    }
  }
  return "Could not load the components";
}

const billed = (t: string) => (t === "ONE_TIME" ? "one-time" : t === "RECURRING" ? "recurring" : t.toLowerCase());

/** Gates grouped by plan family, in the order they first appear. */
function families(gates: Gate[]): [string, Gate[]][] {
  const out = new Map<string, Gate[]>();
  for (const g of gates) out.set(g.family, [...(out.get(g.family) ?? []), g]);
  return [...out.entries()];
}

/**
 * "With residential plans (4): 30 days ₹99 · 90 days ₹280 …" -- one line per
 * plan family. A family with no plan on the list is said plainly: the add-on
 * cannot be sold with it until such a plan exists (radbilling refuses a line
 * whose parent no rule covers).
 */
function GateLines({ gates }: { gates: Gate[] }) {
  return (
    <>
      {families(gates).map(([family, gs]) => {
        const none = gs.every((g) => g.parents.length === 0);
        const count = new Set(gs.flatMap((g) => g.parents.map((c) => c.id))).size;
        return (
          <Typography key={family} variant="body2" component="div" color={none ? "text.secondary" : "text.primary"}>
            With <strong>{family}</strong>
            {none ? "" : ` (${count})`}: {gs.map((g) => (g.detail ? `${g.detail} ${g.price}` : g.price)).join(" · ")}
            {none && (
              <Typography variant="caption" component="div" color="warning.main">
                No such plan is on this list, so it cannot be sold with them yet.
              </Typography>
            )}
          </Typography>
        );
      })}
    </>
  );
}

/**
 * Who may change whether an add-on is internet service (owner 2026-10-27,
 * Internet Distributor review defect 3: "Operator can't flip it"): superadmin
 * and billing admin, as radbilling authorises the write. DISPLAY ONLY.
 */
export function canSetInternet(roles: string[]): boolean {
  return roles.some((r) => ["superadmin", "bssadmin"].includes(r.toLowerCase()));
}

/**
 * The "Internet service" tick (owner 2026-10-17). Ticked: the add-on goes on
 * the sale's internet receipt with the plan; unticked: on a separate receipt
 * of its own. A plan is always internet, so it shows no tick. Anyone but a
 * superadmin or billing admin sees the answer, not the tick (owner 2026-10-27).
 */
function InternetTick({ c, onToggle, busy, may }: { c: PriceComponent; onToggle: (c: PriceComponent, internet: boolean) => void; busy: boolean; may: boolean }) {
  if (!isTickableAddon(c)) {
    return (
      <Typography variant="caption" color="text.secondary">
        {c.radcatalog_groupuid || c.labels?.product ? "always" : "—"}
      </Typography>
    );
  }
  if (!may) {
    return (
      <Typography variant="body2" aria-label={`${c.name} internet service`}>
        {c.pureinternetservice ? "Yes" : "No"}
      </Typography>
    );
  }
  return (
    <Checkbox
      size="small"
      checked={c.pureinternetservice}
      disabled={busy}
      inputProps={{ "aria-label": `${c.name} is internet service` }}
      onChange={(e) => onToggle(c, e.target.checked)}
    />
  );
}

type Toggle = { onToggle: (c: PriceComponent, internet: boolean) => void; busy: boolean; may: boolean };

/** The upstream link (owner 2026-10-18): null when this provider buys from no upstream. */
type Upstream = { items: WalletItem[]; onPick: (c: PriceComponent, key: string) => void; busy: boolean } | null;

const perItem = (it: WalletItem) =>
  `${it.name} — ₹${(it.pricepaise / 100).toFixed(2)}${it.billing === "recurring" ? " per 30 days" : " once"}, ${gstPriceSuffix({ gstratebp: it.gstratebp, priceincludesgst: Boolean(it.priceincludesgst) })}`;

/**
 * "Supplied by your upstream" (owner 2026-10-18: no dropdown). The add-on's
 * one matching upstream item is shown with Link; once linked, a sale holds and
 * charges that price from the wallet, and Unlink stops it. No match: "—".
 */
function UpstreamPick({ c, up }: { c: PriceComponent; up: NonNullable<Upstream> }) {
  if (!isTickableAddon(c)) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  const current = c.labels?.upstream_item ?? "";
  if (current !== "") {
    const it = up.items.find((x) => x.itemkey === current);
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color={it ? "success.main" : "warning.main"}>
          {it ? `✓ ${perItem(it)}` : `${current}: your upstream no longer prices it`}
        </Typography>
        <Button size="small" disabled={up.busy} onClick={() => up.onPick(c, "")} aria-label={`Unlink ${c.name} from your upstream`}>
          Unlink
        </Button>
      </Stack>
    );
  }
  const match = matchUpstreamItem(c, up.items);
  if (!match) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {perItem(match)}
      </Typography>
      <Button size="small" variant="outlined" disabled={up.busy} onClick={() => up.onPick(c, match.itemkey)} aria-label={`Link ${c.name} to your upstream's ${match.name}`}>
        Link
      </Button>
    </Stack>
  );
}

function Section({ title, hint, entries, own, toggle, upstream = null }: { title: string; hint: string; entries: ComponentEntry[]; own: boolean; toggle: Toggle; upstream?: Upstream }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6">
        {title} <Typography component="span" color="text.secondary">({entries.length})</Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {hint}
      </Typography>
      {entries.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label={title}>
            <TableHead>
              <TableRow>
                <TableCell>Component</TableCell>
                <TableCell>Billed</TableCell>
                {own && <TableCell align="right">Price</TableCell>}
                <TableCell>{own ? "Priced differently with a plan" : "Sold with"}</TableCell>
                <TableCell align="center">Internet service</TableCell>
                {upstream && <TableCell>Supplied by your upstream</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.component.id}>
                  <TableCell>
                    {e.component.name}
                    <Typography variant="caption" component="div" color="text.secondary">
                      {e.component.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={billed(e.component.billingtype)} />
                  </TableCell>
                  {own && <TableCell align="right">{e.ownPrice}</TableCell>}
                  <TableCell>
                    {e.gates.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <GateLines gates={e.gates} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <InternetTick c={e.component} onToggle={toggle.onToggle} busy={toggle.busy} may={toggle.may} />
                  </TableCell>
                  {upstream && (
                    <TableCell>
                      <UpstreamPick c={e.component} up={upstream} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/**
 * Plans › Components (plan builder design C4). READ-ONLY, from the same two
 * reads as the price list: what this provider sells on its own -- its plans --
 * and what it sells only with a plan, with that condition said in words.
 *
 * The condition is read from the price rules, which are what radbilling
 * enforces on an invoice line (components.ts). Prices and conditions come
 * from the approved price file (design §9.5 D3); the one write here is the
 * "Internet service" tick on an add-on (owner 2026-10-17), and its link to the
 * upstream's priced item (owner 2026-10-18).
 */
export function ComponentsScreen() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  const [target, setTarget] = useState(own);
  const [ns, setNs] = useState(own);

  const load = useQuery({
    queryKey: ["components", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async ({ signal }) => {
      const [components, rules] = await Promise.all([listPriceComponents(identity, ns, signal), listPriceRules(identity, ns, signal)]);
      return componentsView(components, rules);
    },
  });
  const v = load.data;
  const empty = v && v.independent.length === 0 && v.dependent.length === 0;
  const qc = useQueryClient();
  const tick = useMutation({
    mutationFn: ({ c, internet }: { c: PriceComponent; internet: boolean }) => setComponentInternet(identity, ns, c.id, internet),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["components", ns] }),
  });
  const toggle: Toggle = { onToggle: (c, internet) => tick.mutate({ c, internet }), busy: tick.isPending, may: canSetInternet(identity.roles) };
  // The upstream's priced items, from this provider's own wallet view.
  const wallet = useQuery({
    queryKey: ["wallet", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => getWallet(identity, ns, signal),
  });
  const link = useMutation({
    mutationFn: ({ c, key }: { c: PriceComponent; key: string }) => setComponentUpstreamItem(identity, ns, c.id, key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["components", ns] }),
  });
  const upstream: Upstream = wallet.data?.eligible ? { items: upstreamItemsOf(wallet.data), onPick: (c, key) => link.mutate({ c, key }), busy: link.isPending } : null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Components
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What this provider sells on its own, and what it sells only with a plan, both from the approved price file. An add-on
        that is not "Internet service" (a router, OTT, installation) goes on a separate receipt. Only a superadmin or billing
        admin changes that: it decides the invoice series and AGR.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" label="Namespace" value={target} sx={{ minWidth: 320 }}
          helperText="Your own, or one of your sites"
          onChange={(e) => setTarget(e.target.value.trim().toLowerCase())} />
        <Button variant="outlined" sx={{ height: 40 }} onClick={() => setNs(target)}>
          Open
        </Button>
      </Stack>

      {load.isLoading && <Typography variant="body2">Loading…</Typography>}
      {tick.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>The Internet service tick was not saved</AlertTitle>
          {(tick.error as Error).message}
        </Alert>
      )}
      {link.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>The upstream link was not saved</AlertTitle>
          {(link.error as Error).message}
        </Alert>
      )}
      {upstream && upstream.items.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your upstream has not priced any item yet (Static IP, extra login…), so no add-on can be linked to one.
        </Alert>
      )}
      {load.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{errorTitle(load.error as Error)}</AlertTitle>
          {(load.error as Error).message}
        </Alert>
      )}

      {empty && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>No components are on sale for {ns} yet</AlertTitle>
          Components reach the platform when an approved price list is turned into components (on a test namespace:
          Plans › Propose from a document, Approve). Until then this provider has nothing to sell here.
        </Alert>
      )}

      {v && !empty && (
        <>
          <Section title="Sold on their own" hint="Plans, and anything with a price that needs no plan." entries={v.independent} own toggle={toggle} upstream={upstream} />
          <Section
            title="Sold only with a plan"
            hint="Add-ons and one-time charges. Each is billed under a plan it is sold with; without one the invoice refuses it."
            entries={v.dependent}
            own={false}
            toggle={toggle}
            upstream={upstream}
          />
        </>
      )}

      {v && v.withdrawn.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {v.withdrawn.length} withdrawn component{v.withdrawn.length === 1 ? "" : "s"} not shown:{" "}
          {v.withdrawn.map((c) => c.name).join(", ")}.
        </Typography>
      )}
    </Box>
  );
}
